import React, { useState } from "react";
import {
  Card,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Pagination,
  CircularProgress,
  TextField,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Table from "examples/Tables/Table";
import toast from "react-hot-toast";
import {
  Visibility,
  CalendarToday,
  CheckCircle,
  HourglassEmpty,
  MonetizationOn,
  EmojiEvents,
  Add,
  Layers,
  Engineering,
} from "@mui/icons-material";

import { useGetCreatorsListQuery, useAddCoinsToUserMutation } from "lib/store/service/auth";

function Creators() {
  const [page, setPage] = useState(1);

  // Fetch backend-filtered Level Creators ONLY (GET /api/v1/admin/user/creators)
  const { data: creatorsData, isLoading, isFetching, refetch } = useGetCreatorsListQuery({ page, limit: 10 });
  const [addCoinsToUser, { isLoading: isGrantingCoins }] = useAddCoinsToUserMutation();

  const [selectedCreator, setSelectedCreator] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [coinsToGrant, setCoinsToGrant] = useState(50);

  const rawCreatorsList = creatorsData?.data?.creators || creatorsData?.response?.data?.creators || creatorsData?.data || [];
  const totalPages = creatorsData?.data?.totalPages || creatorsData?.response?.data?.totalPages || 1;
  const totalCreatorsCount = creatorsData?.data?.total || rawCreatorsList.length;

  const handleOpenDetails = (creator) => {
    setSelectedCreator(creator);
    setCoinsToGrant(50);
    setDetailsOpen(true);
  };

  const handleGrantCoins = async () => {
    if (!selectedCreator || !coinsToGrant || coinsToGrant <= 0) {
      toast.error("Please enter a valid coin amount");
      return;
    }

    try {
      const res = await addCoinsToUser({
        userId: selectedCreator._id,
        coinsToAdd: Number(coinsToGrant),
      }).unwrap();

      toast.success(res?.message || `✅ Successfully granted +${coinsToGrant} Logic Coins to ${selectedCreator.name}!`);
      
      setSelectedCreator((prev) => (prev ? { ...prev, coins: (prev.coins || 0) + Number(coinsToGrant) } : prev));
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to grant coins to creator");
    }
  };

  const columns = [
    { name: "LEVEL CREATOR", align: "left" },
    { name: "ROLE TYPE", align: "center" },
    { name: "CREATED LEVELS", align: "center" },
    { name: "APPROVED", align: "center" },
    { name: "CAMPAIGN SOLVED", align: "center" },
    { name: "LOGIC COINS", align: "center" },
    { name: "ACTIONS", align: "center" },
  ];

  const rows = rawCreatorsList.map((creator) => {
    const creatorName = creator.name || creator.username || "Creator";
    const creatorEmail = creator.email || "No email provided";
    const creatorAvatar = creator.picture || creator.avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${creator._id}`;
    const coins = creator.coins !== undefined ? creator.coins : 0;
    const createdLevelsCount = creator.totalCreatedLevels || creator.approvedLevelsCount || 0;
    const approvedCount = creator.approvedLevelsCount || 0;
    const completedCount = creator.completedCount || (creator.completedClassicLevels ? creator.completedClassicLevels.length : 0);

    return {
      "LEVEL CREATOR": (
        <VuiBox display="flex" alignItems="center" gap={2}>
          <Avatar src={creatorAvatar} alt={creatorName} sx={{ width: 38, height: 38 }} />
          <VuiBox display="flex" flexDirection="column">
            <VuiTypography variant="button" color="white" fontWeight="bold">
              {creatorName}
            </VuiTypography>
            <VuiTypography variant="caption" color="text">
              {creatorEmail}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      ),
      "ROLE TYPE": (
        <Chip
          icon={<Engineering style={{ fontSize: 14, color: "#34d399" }} />}
          label="VERIFIED CREATOR 👨‍💻"
          size="small"
          sx={{
            backgroundColor: "rgba(16, 185, 129, 0.2) !important",
            color: "#34d399 !important",
            fontWeight: "bold",
            border: "1px solid #10b981",
          }}
        />
      ),
      "CREATED LEVELS": (
        <Chip
          label={`${createdLevelsCount} Levels`}
          color={createdLevelsCount > 0 ? "info" : "default"}
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      ),
      APPROVED: (
        <VuiTypography variant="caption" color="success" fontWeight="bold">
          ✅ {approvedCount} Approved
        </VuiTypography>
      ),
      "CAMPAIGN SOLVED": (
        <VuiTypography variant="caption" color="warning" fontWeight="bold">
          🏆 {completedCount} Solved
        </VuiTypography>
      ),
      "LOGIC COINS": (
        <VuiTypography variant="button" color="warning" fontWeight="bold">
          🪙 {coins} Coins
        </VuiTypography>
      ),
      ACTIONS: (
        <Button
          variant="contained"
          size="small"
          startIcon={<Visibility />}
          onClick={() => handleOpenDetails(creator)}
          sx={{
            fontSize: "11px",
            fontWeight: "bold",
            backgroundColor: "#06b6d4 !important",
            color: "#ffffff !important",
            borderRadius: "8px",
            px: 2,
            boxShadow: "0 0 12px rgba(6, 182, 212, 0.4)",
            "&:hover": {
              backgroundColor: "#0891b2 !important",
              boxShadow: "0 0 18px rgba(6, 182, 212, 0.7)",
            },
          }}
        >
          View Creator Details
        </Button>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header Title Card */}
        <Card sx={{ p: 3, mb: 3 }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <VuiBox display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ backgroundColor: "rgba(16, 185, 129, 0.2)", color: "#34d399", width: 50, height: 50, border: "1px solid #10b981" }}>
                <Engineering fontSize="large" />
              </Avatar>
              <VuiBox>
                <VuiTypography variant="h4" color="white" fontWeight="bold">
                  👨‍💻 Active Level Creators Directory ({totalCreatorsCount})
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  Direct backend-filtered list of users who have authored circuit levels or hold creator privileges.
                </VuiTypography>
              </VuiBox>
            </VuiBox>

            {(isLoading || isFetching) && (
              <VuiBox display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} color="info" />
                <VuiTypography variant="caption" color="text">
                  Refreshing Creators...
                </VuiTypography>
              </VuiBox>
            )}
          </VuiBox>
        </Card>

        {/* Creator List Table Card */}
        <Card sx={{ p: 3, mb: 3 }}>
          <VuiBox p={1}>
            {rawCreatorsList.length === 0 && !isLoading ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <VuiTypography variant="h3" mb={1}>👨‍💻</VuiTypography>
                <VuiTypography variant="h6" color="white" fontWeight="bold" mb={0.5}>
                  No Active Level Creators Found
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  Community creators will appear here as soon as custom levels are authored.
                </VuiTypography>
              </VuiBox>
            ) : (
              <Table columns={columns} rows={rows} />
            )}
          </VuiBox>
        </Card>

        {totalPages > 1 && (
          <Card sx={{ p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, val) => setPage(val)}
              variant="outlined"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": { color: "#ffffff !important", borderColor: "#334155" },
                "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#06b6d4 !important", color: "#ffffff !important" },
                "& .MuiPaginationItem-root:hover": { backgroundColor: "rgba(6, 182, 212, 0.2)" },
              }}
            />
          </Card>
        )}
      </VuiBox>

      {/* Creator Detailed Profile & Stats Modal Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#0f172a",
            color: "#fff",
            borderRadius: "20px",
            border: "1px solid #06b6d4",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #1e293b", pb: 2 }}>
          <VuiBox display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <VuiBox display="flex" alignItems="center" gap={2}>
              <Avatar
                src={selectedCreator?.picture || selectedCreator?.avatar}
                alt={selectedCreator?.name}
                sx={{ width: 54, height: 54 }}
              />
              <VuiBox display="flex" flexDirection="column">
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  {selectedCreator?.name || selectedCreator?.username || "Creator Profile"}
                </VuiTypography>
                <VuiTypography variant="caption" color="cyan">
                  {selectedCreator?.email || "No email"} (ID: {selectedCreator?._id})
                </VuiTypography>
              </VuiBox>
            </VuiBox>
            <Chip
              icon={<Engineering style={{ fontSize: 14, color: "#34d399" }} />}
              label="VERIFIED CREATOR 👨‍💻"
              color="success"
              size="small"
              sx={{ fontWeight: "bold" }}
            />
          </VuiBox>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          {/* Creator Timestamps & Balance Bar */}
          <VuiBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            mb={3}
            sx={{ backgroundColor: "#1e293b", borderRadius: "12px", border: "1px solid #334155" }}
          >
            <VuiBox display="flex" alignItems="center" gap={1}>
              <CalendarToday sx={{ color: "#38bdf8", fontSize: 18 }} />
              <VuiTypography variant="caption" color="text">
                Joined: <strong style={{ color: "#fff" }}>{selectedCreator?.createdAt ? new Date(selectedCreator.createdAt).toLocaleDateString() : "2026-08-01"}</strong>
              </VuiTypography>
            </VuiBox>

            <VuiBox display="flex" alignItems="center" gap={1}>
              <MonetizationOn sx={{ color: "#f59e0b", fontSize: 20 }} />
              <VuiTypography variant="caption" color="text">
                Logic Coin Balance: <strong style={{ color: "#f59e0b", fontSize: "14px" }}>🪙 {selectedCreator?.coins || 0} Coins</strong>
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          {/* Stats Overview Grid */}
          <VuiBox mb={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2, textAlign: "center", backgroundColor: "#1e293b !important" }}>
                  <Layers sx={{ color: "#38bdf8", fontSize: 24, mb: 0.5 }} />
                  <VuiTypography variant="caption" color="text">TOTAL CREATED</VuiTypography>
                  <VuiTypography variant="h4" color="white" fontWeight="bold">
                    {selectedCreator?.totalCreatedLevels || selectedCreator?.approvedLevelsCount || 0} Levels
                  </VuiTypography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2, textAlign: "center", backgroundColor: "#1e293b !important" }}>
                  <CheckCircle sx={{ color: "#10b981", fontSize: 24, mb: 0.5 }} />
                  <VuiTypography variant="caption" color="text">APPROVED LEVELS</VuiTypography>
                  <VuiTypography variant="h4" color="success" fontWeight="bold">
                    {selectedCreator?.approvedLevelsCount || 0}
                  </VuiTypography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2, textAlign: "center", backgroundColor: "#1e293b !important" }}>
                  <HourglassEmpty sx={{ color: "#f59e0b", fontSize: 24, mb: 0.5 }} />
                  <VuiTypography variant="caption" color="text">PENDING MODERATION</VuiTypography>
                  <VuiTypography variant="h4" color="warning" fontWeight="bold">
                    {selectedCreator?.pendingLevelsCount || 0}
                  </VuiTypography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2, textAlign: "center", backgroundColor: "#1e293b !important" }}>
                  <EmojiEvents sx={{ color: "#ec4899", fontSize: 24, mb: 0.5 }} />
                  <VuiTypography variant="caption" color="text">CAMPAIGN SOLVED</VuiTypography>
                  <VuiTypography variant="h4" color="white" fontWeight="bold">
                    {selectedCreator?.completedCount || (selectedCreator?.completedClassicLevels ? selectedCreator.completedClassicLevels.length : 0)}
                  </VuiTypography>
                </Card>
              </Grid>
            </Grid>
          </VuiBox>

          <Divider sx={{ bg: "#334155", my: 2 }} />

          {/* Quick Admin Coin Top-Up Section */}
          <VuiBox p={2.5} sx={{ backgroundColor: "#1e293b", borderRadius: "14px", border: "1px solid #334155" }}>
            <VuiTypography variant="button" color="white" fontWeight="bold" mb={1} display="block">
              🪙 Directly Credit Logic Coins to {selectedCreator?.name}
            </VuiTypography>
            <VuiBox display="flex" gap={2} alignItems="center" mt={1}>
              <TextField
                type="number"
                size="small"
                value={coinsToGrant}
                onChange={(e) => setCoinsToGrant(e.target.value)}
                placeholder="Coins (e.g. 50)"
                sx={{
                  backgroundColor: "#0f172a",
                  borderRadius: "8px",
                  input: { color: "#f59e0b", fontWeight: "bold" },
                  maxWidth: "180px",
                }}
              />
              <Button
                variant="contained"
                disabled={isGrantingCoins}
                onClick={handleGrantCoins}
                startIcon={<Add />}
                sx={{
                  backgroundColor: "#f59e0b !important",
                  color: "#ffffff !important",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  px: 3,
                  boxShadow: "0 0 12px rgba(245, 158, 11, 0.4)",
                  "&:hover": { backgroundColor: "#d97706 !important" },
                }}
              >
                {isGrantingCoins ? "Granting..." : "Grant Coins 🪙"}
              </Button>
            </VuiBox>
          </VuiBox>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button
            onClick={() => setDetailsOpen(false)}
            sx={{ color: "#94a3b8", fontWeight: "bold" }}
          >
            Close Profile
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Creators;
